import type { MerchantRiskCatalogItem } from "@unbox/api";

export type RiskSuggestion = {
  riskId: string;
  readableName: string;
  enabled: boolean;
  matched: string;
};

const MAX_SUGGESTIONS = 5;

// Each phrase pattern points at the catalog risks it describes, most specific first. A risk id
// the merchant's catalog doesn't contain is skipped, so customs and retired risks are safe here.
const HINTS: [RegExp, string[]][] = [
  [/\bai\b|a\.i\.|ai-generated|generated|artificial|fake (video|person|voice)|deepfake|avatar|\bIA\b/i, ["ai_content"]],
  [
    /logo|third.?part|3rd.?part|this part(y|ies)|\bip'?s?\b|other (brands?|products)|different (brands?|products|ip)|multiple (products|brands)|\bhaul\b|comparison|supermarket|wearing|competitor|nespresso|nesquik|nutella|shirt|hoodie|partner(ship)?|collab|sponsor|intellectual property|licensed brand/i,
    ["third_party_logo_present"],
  ],
  [
    /competitor|better than|worse than|compared? (to|with)|comparison|\bvs\.?\b|versus|instead of|switch(ed)? from|(trash|slat|bash|knock)(s|ing|ed)? (another|other|a different)/i,
    ["competitor_sentiment"],
  ],
  [
    /(?<!price )claims?\b|cure|heals?\b|whiten|whiter|clinically|proven|guarantee|factually|not true|misleading|relief|bloat|constipat|tired|energy|weight loss|glp|effective/i,
    ["banned_claims", "negative_product_claims", "harmful_product_claims"],
  ],
  [
    /harmful|unsafe|not safe|side.?effects?|overdose|allerg|dangerous (to|for)|instead of (medicine|medication|a doctor)|replace (your )?(medicine|medication|meals?)/i,
    ["negative_product_claims", "harmful_product_claims"],
  ],
  [/retailer|walmart|target|amazon|tesco|boots|sainsbury|costco|in.?store/i, ["negative_retailer_sentiment"]],
  [
    /(explicit|swear|curs|rude|dirty|inappropriate|offensive|vulgar|sexual).{0,25}(song|lyrics|music|track|sound)|(song|lyrics|music|track|sound).{0,25}(explicit|swear|curs|rude|dirty|inappropriate|offensive|vulgar|sexual)|uncensored|nsfw/i,
    ["explicit_lyrics", "profanity_language"],
  ],
  [/music|song|lyrics|soundtrack|\baudio\b/i, ["unlicensed_popular_music_detection"]],
  [
    /swear|curs(e|ing)|profan|bad language|f\*|explicit|fuck|shit|mother.?f|inappropriate (song )?lyrics/i,
    ["profanity_language"],
  ],
  [/banned|keyword|forbidden word|uses (the )?(word|term)/i, ["banned_keywords"]],
  [/\b(dogs?|cats?|pets?|animals?|puppy|puppies|kitten|horse|bird)\b/i, ["animal_presence"]],
  [/urgen|sells? out|selling out|quick|last chance|hurry|running out|limited time/i, ["urgency_tactics"]],
  [
    /challenge|chug|speed.?eat|eating contest|mukbang|\bdares?\b|as fast as/i,
    ["challenge_consumption", "overconsumption"],
  ],
  [
    /too (much|many)|whole (bag|box|pack|tub|jar|bottle)|entire (bag|box|pack|tub|jar|bottle)|binge|overeat|over.?consum|eats? (it )?all|excessive|in one (go|sitting)|(several|multiple|\d+) (bottles|cans|packs|bars|servings)/i,
    ["overconsumption"],
  ],
  [
    /alcohol|\bbeers?\b|\bwine\b|vodka|cocktail|\bdrunk\b|tipsy|spirits|champagne|prosecco|\bgin\b|whiske?y|\brum\b|tequila|smok|cigar|\bvap(e|es|ing)\b|tobacco|nicotine|\bweed\b|cannabis/i,
    ["alcohol_tobacco"],
  ],
  [/danger|knife|misus|spill|pour|throw|waste of food|serving|scoop|positioned|cannot be done/i, ["food_misuse"]],
  [
    /\b(child|children|kids?|minors?|teens?|teenagers?)\b.{0,40}\b(speak|talk|present|host|narrat|spokes)|\b(speak|talk|present|host|narrat|spokes)\w*\b.{0,40}\b(child|children|kids?|minors?|teens?|teenagers?)\b/i,
    ["child_spokesperson"],
  ],
  [
    /\b(child|children|kids?|baby|babies|toddler|minors?|teens?|teenagers?)\b.{0,40}\b(eat|drink|consum|taste|tasting|holding|uses?|using)|\b(eat|drink|consum|taste|tasting)\w*\b.{0,40}\b(child|children|kids?|baby|babies|toddler|minors?|teens?|teenagers?)\b/i,
    ["child_participant"],
  ],
  [
    /\b(child|children|kids?|baby|babies|toddler|minors?|teens?|teenagers?|school ?(kids?|girl|boy)|under.?18|underage)\b/i,
    ["child_participant", "child_spokesperson", "child_appeal"],
  ],
  [
    /aimed at (kids|children|teens)|for kids|cartoon|\btoys?\b|playground|young (audience|viewers)|childish/i,
    ["child_appeal"],
  ],
  [
    /pregnan|elderly|older (people|person|adults?|woman|man)|grand(ma|pa|mother|father|parent)|disab|wheelchair|\bsick\b|\bill\b|patient|vulnerable|breastfeed|nursing mother/i,
    ["vulnerable_group"],
  ],
  [
    /disclos|#ad\b|\bad (tag|label)|not (labell?ed|marked|tagged) as (an )?ad|sponsored|paid partnership|gifted|affiliate|commission|didn'?t say (it'?s|it was) (an )?ad/i,
    ["ftc_disclosure"],
  ],
];

/** Every risk id a phrase points at, so a test can check they still exist. */
export const SUGGESTED_RISK_IDS = [...new Set(HINTS.flatMap(([, riskIds]) => riskIds))];

// Words too generic to tie a description to a risk by name.
const STOP_WORDS = new Set([
  "video",
  "product",
  "products",
  "creator",
  "there",
  "with",
  "this",
  "that",
  "from",
  "about",
  "their",
  "brand",
  "content",
  "shown",
]);

/**
 * Ranks the merchant's catalog risks against a reviewer's description of what the AI missed. Phrase
 * patterns come first, in their listed order; after them, any risk whose name shares a word of four
 * letters or more with the description. A custom risk is also matched on its description, since
 * its name is the merchant's own and may not use the words reviewers type.
 */
export function suggestRisks(description: string, catalog: MerchantRiskCatalogItem[]): RiskSuggestion[] {
  const text = description.trim();
  if (!text) return [];

  const byId = new Map(catalog.map((risk) => [risk.id, risk]));
  const suggestions = new Map<string, RiskSuggestion>();
  const add = (risk: MerchantRiskCatalogItem, matched: string) => {
    if (suggestions.has(risk.id)) return;
    suggestions.set(risk.id, { riskId: risk.id, readableName: risk.readableName, enabled: risk.enabled, matched });
  };

  for (const [pattern, riskIds] of HINTS) {
    const match = text.match(pattern);
    if (!match) continue;
    for (const riskId of riskIds) {
      const risk = byId.get(riskId);
      if (risk) add(risk, match[0]);
    }
  }

  const words = text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 3 && !STOP_WORDS.has(word));
  for (const risk of catalog) {
    const haystack = (
      risk.category === "custom" ? `${risk.readableName} ${risk.readableDescription}` : risk.readableName
    ).toLowerCase();
    const word = words.find((candidate) => haystack.includes(candidate));
    if (word) add(risk, word);
  }

  return [...suggestions.values()].slice(0, MAX_SUGGESTIONS);
}
